//#region redux
//#endregion
//#region types
import { InvoiceFormTypes, MissingDocumentFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { FormFieldConstants } from "utils/constants";
import { UploadedFile } from "models/utilTypes/fileUtilsTypes";
import { WintSuperkollAPIModel as ApiModel } from "wint-api-client/dist/types";
import { ArraySchema } from "models/utilTypes/yupSchemaTypes";
import { ReceiptPerson } from "models/formTypes/receipt";
import { DimensionMap, ForcedDimensions } from "models/propTypes/dimensionPropTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
//#endregion
//#region components
//#endregion
import * as Yup from "yup";
import { DateHelper } from "utils/helpers";

function useTodoFormValidationSchema(
  missingDocument: ApiModel.MissingDocument,
  dimensions: {
    receiptForcedDimensionMap: ForcedDimensions[];
    invoiceForcedDimensionMap: ForcedDimensions[];
    recieptDimensions: DimensionMap;
    invoiceDimensions: DimensionMap;
  }
) {
  const requiredOnType = (schema: Yup.AnySchema, type: ApiModel.MissingDocumentPutType) => {
    return schema.when(FormFieldConstants.MissingDocument.Todo.Type, ([t], schema: Yup.AnySchema) =>
      t === type ? schema.required(TranslationKeys.Validations.Required) : schema
    );
  };

  const validationSchema = Yup.object<MissingDocumentFormTypes.Todo>({
    explanation: Yup.string().when(
      [
        FormFieldConstants.MissingDocument.Todo.Type,
        FormFieldConstants.MissingDocument.Todo.PostWithoutPicture,
        FormFieldConstants.MissingDocument.Todo.FilesToUpload,
      ],
      ([type, withoutPicture, filesToUpload], schema: Yup.StringSchema) => {
        return (
          ((withoutPicture ||
            type === ApiModel.MissingDocumentPutType.Other ||
            (missingDocument.hasMatchedDocument &&
              !filesToUpload?.filter((f: UploadedFile) => !f.skipOnFormSubmit).length)) &&
            schema.required(TranslationKeys.Validations.Required)) ||
          schema
        );
      }
    ),
    postWithoutPicture: Yup.boolean(),
    type: Yup.number().when(
      FormFieldConstants.MissingDocument.Todo.FilesToUpload,
      ([files], schema: Yup.NumberSchema) =>
        (files &&
          (files.length > 0 || !!missingDocument.attachments?.length) &&
          !missingDocument.hasMatchedDocument &&
          missingDocument.amount < 0 &&
          schema.required(TranslationKeys.Validations.Required)) ||
        schema
    ),
    currency: requiredOnType(Yup.string(), ApiModel.MissingDocumentPutType.Receipt),
    filesToUpload: Yup.array()
      .of(
        Yup.object<UploadedFile>({
          contentType: Yup.string(),
          fileName: Yup.string(),
          fileSize: Yup.number(),
          data: Yup.string(),
        })
      )
      .when(
        FormFieldConstants.MissingDocument.Todo.PostWithoutPicture,
        ([postWithoutPicture]: [boolean], schema: ArraySchema<UploadedFile>) =>
          (!postWithoutPicture &&
            !missingDocument.attachments?.length &&
            schema.required(TranslationKeys.Validations.Required)) ||
          schema
      ),

    receipt: Yup.object<MissingDocumentFormTypes.Receipt>().when(
      FormFieldConstants.MissingDocument.Todo.Type,
      ([type], schema: Yup.ObjectSchema<{}, MissingDocumentFormTypes.Receipt>) =>
        type === ApiModel.MissingDocumentPutType.Receipt
          ? schema.shape({
              category: Yup.number().required(TranslationKeys.Validations.Required),
              persons: Yup.array()
                .of(
                  Yup.object<ReceiptPerson>({
                    id: Yup.number(),
                    name: Yup.string().required(TranslationKeys.Validations.Required),
                    company: Yup.string().required(TranslationKeys.Validations.Required),
                  })
                )
                .when(
                  [FormFieldConstants.MissingDocument.Receipt.CategoryType],
                  ([value]: [ApiModel.ReceiptCategoryType], schema: ArraySchema<ReceiptPerson>) =>
                    ((value === ApiModel.ReceiptCategoryType.Representation ||
                      value === ApiModel.ReceiptCategoryType.EmployeeRepresentation) &&
                      schema
                        .required(TranslationKeys.Validations.Required)
                        .min(1, TranslationKeys.Validations.AtLeastOneItem)) ||
                    schema
                ),

              intent: Yup.string().when(
                [FormFieldConstants.MissingDocument.Receipt.CategoryType],
                ([value], schema: Yup.StringSchema) =>
                  ((value === ApiModel.ReceiptCategoryType.Representation ||
                    value === ApiModel.ReceiptCategoryType.EmployeeRepresentation) &&
                    schema.required(TranslationKeys.Validations.Required)) ||
                  schema
              ),

              date: Yup.date()
                .typeError(TranslationKeys.Validations.InvalidDate)
                .required(TranslationKeys.Validations.Required),
              dimensions: Yup.array()
                .of(
                  Yup.object<ApiModel.DimensionData>({
                    dimensionId: Yup.string(),
                    amount: Yup.number(),
                  })
                )
                // eslint-disable-next-line no-empty-pattern
                .when([], ([], schema: ArraySchema<ApiModel.DimensionData>) =>
                  dimensions.receiptForcedDimensionMap.length > 0 ? schema.required("") : schema
                )
                .checkForcedDimensions(dimensions.receiptForcedDimensionMap)
                .validateDimensionAmounts(dimensions.recieptDimensions, missingDocument.amount),
            })
          : schema
    ),

    incomingInvoice: Yup.object<MissingDocumentFormTypes.IncomingInvoice>().when(
      FormFieldConstants.MissingDocument.Todo.Type,
      ([type], schema: Yup.ObjectSchema<{}, MissingDocumentFormTypes.IncomingInvoice>) =>
        type === ApiModel.MissingDocumentPutType.IncomingInvoice
          ? schema.shape({
              category: Yup.object<AutocompleteListItem>().required(TranslationKeys.Validations.Required),
              dimensions: Yup.array()
                .of(
                  Yup.object({
                    dimensionId: Yup.string(),
                    amount: Yup.number(),
                  })
                )
                .checkForcedDimensions(dimensions.invoiceForcedDimensionMap)
                .validateDimensionAmounts(dimensions.invoiceDimensions, missingDocument.amount),
              periodisation: Yup.mixed<InvoiceFormTypes.InvoicePeriodisation>()
                .test(
                  "complete-range",
                  TranslationKeys.Common.MustSelectDateRange,
                  (value) => !value?.range || !!value.range.start === !!value.range.end
                )
                .test("range-order", TranslationKeys.Validations.PeriodisationEndBeforeStart, (value) => {
                  const { start, end } = value?.range ?? {};
                  if (!start || !end) return true;
                  // Core rejects a date range whose end equals its start; the picker ends a range at end of day, so a
                  // one-day range still passes.
                  if (value?.mode === "date") return new Date(end).getTime() > new Date(start).getTime();
                  return DateHelper.getYearMonth(end) >= DateHelper.getYearMonth(start);
                }),
            })
          : schema
    ),

    other: Yup.object<MissingDocumentFormTypes.MissingDocumentOther>().when(
      FormFieldConstants.MissingDocument.Todo.Type,
      ([type], schema: Yup.ObjectSchema<{}, MissingDocumentFormTypes.MissingDocumentOther>) =>
        type === ApiModel.MissingDocumentPutType.Other
          ? schema.shape({
              dimensions: Yup.array()
                .of(
                  Yup.object<ApiModel.DimensionData>({
                    dimensionId: Yup.string(),
                    amount: Yup.number(),
                  })
                )
                .validateDimensionAmounts(dimensions.invoiceDimensions, missingDocument.amount),
            })
          : schema
    ),
  });

  return validationSchema;
}

export default useTodoFormValidationSchema;
